import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  alpha,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  History as HistoryIcon,
  Assignment as AssignmentIcon,
  Feedback as FeedbackIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  CalendarToday as CalendarTodayIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  AttachFile as AttachFileIcon
} from '@mui/icons-material';
import { useAppContext } from './GrowthSessionManagement';

const CommonHistoryDialog = ({ 
  open, 
  onClose, 
  selectedUser,
  title = "Growth Conversation History",
  type = "growth_session" // "growth_session" or "feedback"
}) => {
  const { growthSessions, users, getLeadById } = useAppContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const [userHistory, setUserHistory] = useState([]);
  const [expandedAccordion, setExpandedAccordion] = useState(null);

  // Get user's complete history when dialog opens
  useEffect(() => {
    if (selectedUser && open) {
      console.log('Loading history for user:', selectedUser);
      
      // Get all Growth Conversations for this user
      const userSessions = growthSessions.filter(session => 
        session.user_id === selectedUser.id || session.user_id === selectedUser.email
      );

      // Sort by date (newest first)
      const sortedSessions = userSessions.sort((a, b) => 
        new Date(b.date || b.created_at) - new Date(a.date || a.created_at)
      );

      console.log('User sessions found:', sortedSessions);
      setUserHistory(sortedSessions);
    }
  }, [selectedUser, open, growthSessions]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'pip_created': return '#4caf50'; // Green - Completed
      case 'growth_session': return '#ff9800'; // Orange - Started/In Progress
      case 'lead_assigned': return '#2196f3'; // Blue - Pending
      case 'completed': return '#4caf50'; // Green
      case 'in_progress': return '#ff9800'; // Orange
      case 'draft': return theme.palette.grey[500];
      case 'scheduled': return theme.palette.primary.main;
      default: return theme.palette.grey[500];
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
      case 'growth_session':
      case 'pip_created':
        return <CheckCircleIcon sx={{ fontSize: 20 }} />;
      case 'in_progress':
        return <ScheduleIcon sx={{ fontSize: 20 }} />;
      case 'draft':
        return <AssignmentIcon sx={{ fontSize: 20 }} />;
      case 'assignment':
      case 'lead_assigned':
        return <PersonIcon sx={{ fontSize: 20 }} />;
      case 'scheduled':
        return <CalendarTodayIcon sx={{ fontSize: 20 }} />;
      default:
        return <FeedbackIcon sx={{ fontSize: 20 }} />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const hasPIPData = (session) => {
    // If status is pip_created, always show PIP section
    console.log(session);
    if (session.status === 'pip_created') return true;
    if (!session.pip_data) return false;
    try {
      const pipData = typeof session.pip_data === 'string' 
        ? JSON.parse(session.pip_data) 
        : session.pip_data;
      return pipData && (pipData.final_decision || pipData.location || (pipData.attached_files && pipData.attached_files.length > 0));
    } catch (error) {
      console.error('Error parsing PIP data:', error);
      return false;
    }
  };

  const getPIPData = (session) => {
    console.log('Getting PIP data for session:', session);
    if (!session.pip_data) {
      // If status is pip_created but no pip_data, return empty object to show PIP section
      return session.status === 'pip_created' ? {} : null;
    }
    
    try {
      return typeof session.pip_data === 'string' 
        ? JSON.parse(session.pip_data) 
        : session.pip_data;
    } catch (error) {
      console.error('Error parsing PIP data:', error);
      // If status is pip_created but data is corrupted, still show empty PIP section
      return session.status === 'pip_created' ? {} : null;
    }
  };

  const getSessionData = (session) => {
    if (!session.session_data) return null;
    return typeof session.session_data === 'string'
      ? JSON.parse(session.session_data)
      : session.session_data;
  };

  // Helper function to safely render values that might be objects
  const renderValue = (value) => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    if (Array.isArray(value)) {
      // Handle arrays of objects or simple values
      return value.map((item, index) => {
        if (typeof item === 'object' && item !== null) {
          // For objects, check for label: value pattern
          if (item.label && item.value) {
            return `${item.label}: ${item.value}`;
          }
          if (item.name && item.value) {
            return `${item.name}: ${item.value}`;
          }
          if (item.title && item.value) {
            return `${item.title}: ${item.value}`;
          }
          return `Item ${index + 1}`;
        }
        return String(item);
      }).join(', ');
    }
    if (typeof value === 'object') {
      // Handle objects by checking for label: value pattern first
      try {
        if (value.label && value.value) {
          return `${value.label}: ${value.value}`;
        }
        if (value.name && value.value) {
          return `${value.name}: ${value.value}`;
        }
        if (value.title && value.value) {
          return `${value.title}: ${value.value}`;
        }

        // If no label-value pattern, display key-value pairs
        const entries = Object.entries(value);
        if (entries.length === 0) return 'N/A';
        return entries.map(([key, val]) => `${key}: ${val}`).join(', ');
      } catch (error) {
        return '[Complex Object]';
      }
    }
    return String(value);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '70vh',
          maxHeight: '90vh',
          width: { xs: '95vw', sm: '90vw', md: '85vw', lg: '80vw' },
          mx: 'auto'
        }
      }}
    >
      <DialogTitle 
        sx={{
          background: `linear-gradient(135deg, #0a0068 0%, #cc4049 100%)`,
          color: 'white',
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <HistoryIcon />
          <Typography variant="h6" fontWeight="bold">
            {title}: {selectedUser?.name}
          </Typography>
        </Box>
        <Button
          onClick={onClose}
          sx={{ 
            color: 'white',
            minWidth: 'auto',
            p: 1,
            '&:hover': { bgcolor: alpha('#ffffff', 0.1) }
          }}
        >
          <CloseIcon />
        </Button>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {userHistory.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <HistoryIcon sx={{ fontSize: 48, color: theme.palette.grey[400], mb: 2 }} />
            <Typography variant="h6" color={theme.palette.text.secondary} gutterBottom>
              No History Found
            </Typography>
            <Typography variant="body2" color={theme.palette.text.secondary}>
              {selectedUser?.name} doesn't have any Growth Conversations yet.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
            {userHistory.map((session, index) => {
              console.log(session);
              const sessionData = getSessionData(session);
              const pipData = getPIPData(session);
              const lead = getLeadById && getLeadById(session.lead_id);

              return (
                <Box key={session.id} sx={{ mb: { xs: 2, md: 3 }, position: 'relative' }}>

                  <Stack 
                    direction="row"
                    spacing={2}
                    sx={{ position: 'relative', zIndex: 1 }}
                  >
                    {/* Timeline Avatar */}
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      alignItems: 'center',
                      width: 120, // Fixed width for all sessions
                      minWidth: 120, // Ensure minimum width
                      maxWidth: 120, // Ensure maximum width
                      position: 'relative',
                      py: 1
                    }}>
                      {/* Status indicator background */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          bgcolor: getStatusColor(session.status),
                          boxShadow: `0 0 12px ${alpha(getStatusColor(session.status), 0.4)}`,
                          zIndex: 2
                        }}
                      />
                      
                      {/* Main Avatar */}
                      <Avatar 
                        sx={{ 
                          bgcolor: getStatusColor(session.status),
                          width: 48,
                          height: 48,
                          mb: 1.5,
                          border: `4px solid ${alpha(getStatusColor(session.status), 0.15)}`,
                          boxShadow: `0 4px 12px ${alpha(getStatusColor(session.status), 0.3)}`,
                          position: 'relative',
                          zIndex: 2,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'scale(1.05)',
                            boxShadow: `0 6px 20px ${alpha(getStatusColor(session.status), 0.4)}`
                          },
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: -2,
                            left: -2,
                            right: -2,
                            bottom: -2,
                            borderRadius: '50%',
                            background: `conic-gradient(from 0deg, ${getStatusColor(session.status)}, ${alpha(getStatusColor(session.status), 0.3)}, ${getStatusColor(session.status)})`,
                            zIndex: -1,
                            opacity: 0.6,
                            animation: 'rotate 3s linear infinite'
                          },
                          '@keyframes rotate': {
                            '0%': { transform: 'rotate(0deg)' },
                            '100%': { transform: 'rotate(360deg)' }
                          }
                        }}
                      >
                        {getStatusIcon(session.status)}
                      </Avatar>
                      
                      {/* Date and Review Period */}
                      <Box sx={{ 
                        textAlign: 'center',
                        width: '100%', // Take full width of container
                        minHeight: 65, // Fixed minimum height for consistency
                        p: 1,
                        bgcolor: alpha(getStatusColor(session.status), 0.05),
                        borderRadius: 2,
                        border: `1px solid ${alpha(getStatusColor(session.status), 0.1)}`,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                      }}>
                        {/* Main Date - Always show */}
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            fontSize: '0.75rem', 
                            fontWeight: 700,
                            color: getStatusColor(session.status),
                            display: 'block',
                            mb: 0.5
                          }}
                        >
                          {session.date ? formatDate(session.date) : (session.created_at ? formatDate(session.created_at) : 'No Date')}
                        </Typography>
                        
                        {/* Review Period Section - Always reserve space */}
                        <Box sx={{ minHeight: 32, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                          {session.review_period_from && session.review_period_to ? (
                            <>
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  fontSize: '0.65rem',
                                  color: theme.palette.text.secondary,
                                  display: 'block',
                                  lineHeight: 1.2,
                                  mb: 0.25
                                }}
                              >
                                Review Period
                              </Typography>
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  fontSize: '0.65rem',
                                  color: theme.palette.text.secondary,
                                  display: 'block',
                                  fontWeight: 500,
                                  lineHeight: 1.1
                                }}
                              >
                                {formatDate(session.review_period_from)} - {formatDate(session.review_period_to)}
                              </Typography>
                            </>
                          ) : (
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                fontSize: '0.65rem',
                                color: alpha(theme.palette.text.secondary, 0.6),
                                display: 'block',
                                fontStyle: 'italic'
                              }}
                            >
                              Status: {session.status?.charAt(0).toUpperCase() + session.status?.slice(1).replace('_', ' ')}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>

                    {/* Content Card */}
                    <Box sx={{ flex: 1, width: { xs: '100%', sm: 'auto' } }}>
                      <Card 
                        elevation={2} 
                        variant="outlined" 
                        sx={{ 
                          mb: 2, 
                          border: `1px solid ${alpha(getStatusColor(session.status), 0.3)}`,
                          borderLeft: `4px solid ${getStatusColor(session.status)}`,
                          backgroundColor: alpha(getStatusColor(session.status), 0.02)
                        }}
                      >
                        <CardContent>
                          {/* Header */}
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'flex-start', sm: 'center' },
                            gap: { xs: 1, sm: 0 },
                            mb: 2 
                          }}>
                            <Box sx={{ flex: 1, mb: { xs: 1, sm: 0 } }}>
                              <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, color: getStatusColor(session.status) }}>
                                Growth Conversation #{session.id}
                              </Typography>
                              {lead && (
                                <Typography variant="body2" sx={{ fontSize: '0.8rem', color: theme.palette.text.secondary }}>
                                  <TrendingUpIcon sx={{ fontSize: 14, mr: 0.5 }} />
                                  Lead: {lead.name}
                                </Typography>
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              <Chip 
                                label={session.status?.charAt(0).toUpperCase() + session.status?.slice(1).replace('_', ' ')} 
                                size="small"
                                sx={{ 
                                  bgcolor: alpha(getStatusColor(session.status), 0.1),
                                  color: getStatusColor(session.status),
                                  fontWeight: 600,
                                  fontSize: '0.75rem',
                                  border: `1px solid ${alpha(getStatusColor(session.status), 0.3)}`
                                }}
                              />
                              {hasPIPData(session) && session.status !== 'lead_assigned' && (
                                <Chip 
                                  label="PIP" 
                                  size="small"
                                  icon={<WarningIcon sx={{ fontSize: 12 }} />}
                                  sx={{ 
                                    bgcolor: alpha(theme.palette.warning.main, 0.1),
                                    color: theme.palette.warning.main,
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                    border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`
                                  }}
                                />
                              )}
                            </Box>
                          </Box>

                          {/* Session Details Accordion */}
                          {sessionData && (
                            <Accordion 
                              expanded={expandedAccordion === `session-${session.id}`}
                              onChange={() => setExpandedAccordion(
                                expandedAccordion === `session-${session.id}` ? null : `session-${session.id}`
                              )}
                              sx={{ mb: 1, boxShadow: 'none', border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}` }}
                            >
                              <AccordionSummary 
                                expandIcon={<ExpandMoreIcon />}
                                sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <FeedbackIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                                  <Typography variant="body2" sx={{ fontSize: '0.875rem', color: theme.palette.primary.main, fontWeight: 500 }}>
                                    View Session Details
                                  </Typography>
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails sx={{ p: 2 }}>
                                <Grid container spacing={{ xs: 1, sm: 2 }}>
                                  {/* Review Period */}
                                  {(session.review_period_from || session.review_period_to) && (
                                    <Grid item xs={12}>
                                      <Box sx={{ p: 1.5, bgcolor: alpha(theme.palette.grey[600], 0.05), borderRadius: 1, border: `1px solid ${alpha(theme.palette.grey[600], 0.1)}` }}>
                                        <Typography variant="caption" sx={{ color: theme.palette.grey[700], fontWeight: 600, fontSize: '0.75rem' }}>
                                          Review Period:
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontSize: '0.8rem', mt: 0.5 }}>
                                          {formatDate(session.review_period_from)} - {formatDate(session.review_period_to)}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  )}
                                  {sessionData.key_strengths && (
                                    <Grid item xs={12} md={6}>
                                      <Box sx={{ p: 1.5, bgcolor: alpha(theme.palette.success.main, 0.05), borderRadius: 1, border: `1px solid ${alpha(theme.palette.success.main, 0.1)}` }}>
                                        <Typography variant="caption" sx={{ color: theme.palette.success.main, fontWeight: 600, fontSize: '0.75rem' }}>
                                          Key Strengths:
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontSize: '0.8rem', mt: 0.5 }}>
                                          {sessionData.key_strengths}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  )}
                                  {sessionData.areas_for_improvement && (
                                    <Grid item xs={12} md={6}>
                                      <Box sx={{ p: 1.5, bgcolor: alpha(theme.palette.warning.main, 0.05), borderRadius: 1, border: `1px solid ${alpha(theme.palette.warning.main, 0.1)}` }}>
                                        <Typography variant="caption" sx={{ color: theme.palette.warning.main, fontWeight: 600, fontSize: '0.75rem' }}>
                                          Areas for Improvement:
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontSize: '0.8rem', mt: 0.5 }}>
                                          {sessionData.areas_for_improvement}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  )}
                                  {sessionData.recommended_action_plan && (
                                    <Grid item xs={12}>
                                      <Box sx={{ p: 1.5, bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: 1, border: `1px solid ${alpha(theme.palette.info.main, 0.1)}` }}>
                                        <Typography variant="caption" sx={{ color: theme.palette.info.main, fontWeight: 600, fontSize: '0.75rem' }}>
                                          Recommended Action Plan:
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontSize: '0.8rem', mt: 0.5 }}>
                                          {sessionData.recommended_action_plan}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  )}
                                  {sessionData.support_required && (
                                    <Grid item xs={12} lg={6}>
                                      <Box sx={{ p: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 1, border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}` }}>
                                        <Typography variant="caption" sx={{ color: theme.palette.primary.main, fontWeight: 600, fontSize: '0.75rem' }}>
                                          Support Required:
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontSize: '0.8rem', mt: 0.5 }}>
                                          {sessionData.support_required}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  )}
                                  {sessionData.suggested_plan_of_action && (
                                    <Grid item xs={12} lg={6}>
                                      <Box sx={{ p: 1.5, bgcolor: alpha(theme.palette.secondary.main, 0.05), borderRadius: 1, border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}` }}>
                                        <Typography variant="caption" sx={{ color: theme.palette.secondary.main, fontWeight: 600, fontSize: '0.75rem' }}>
                                          Suggested Plan of Action:
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontSize: '0.8rem', mt: 0.5 }}>
                                          {sessionData.suggested_plan_of_action}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  )}
                                  {sessionData.dynamic_fields && Object.keys(sessionData.dynamic_fields).length > 0 && (
                                    <Grid item xs={12}>
                                      <Box sx={{ p: 1.5, bgcolor: alpha(theme.palette.grey[600], 0.05), borderRadius: 1, border: `1px solid ${alpha(theme.palette.grey[600], 0.1)}` }}>
                                        <Typography variant="caption" sx={{ color: theme.palette.grey[700], fontWeight: 600, fontSize: '0.75rem', mb: 1, display: 'block' }}>
                                          Additional Details:
                                        </Typography>
                                        {Array.isArray(sessionData.dynamic_fields) ? (
                                          sessionData.dynamic_fields.map((item, index) => (
                                            <Box key={index} sx={{ mb: 2 }}>
                                              <Typography variant="body2" sx={{ fontSize: '0.75rem', color: theme.palette.text.primary }}>
                                                {renderValue(item)}
                                              </Typography>
                                            </Box>
                                          ))
                                        ) : (
                                          Object.entries(sessionData.dynamic_fields).map(([key, value]) => (
                                            <Box key={key} sx={{ mb: 2 }}>
                                              <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600, color: theme.palette.text.secondary, display: 'block', mb: 0.5 }}>
                                                {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                              </Typography>
                                              <Typography variant="body2" sx={{ fontSize: '0.75rem', color: theme.palette.text.primary }}>
                                                {renderValue(value)}
                                              </Typography>
                                            </Box>
                                          ))
                                        )}
                                      </Box>
                                    </Grid>
                                  )}
                                </Grid>
                              </AccordionDetails>
                            </Accordion>
                          )}

                          {/* Manager Comments */}
                          {session.manager_comments && (
                            <Box sx={{ mt: 2, p: 2, bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: 1, border: `1px solid ${alpha(theme.palette.info.main, 0.2)}` }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <FeedbackIcon sx={{ fontSize: 16, color: theme.palette.info.main }} />
                                <Typography variant="caption" sx={{ color: theme.palette.info.main, fontSize: '0.75rem', fontWeight: 600 }}>
                                  Manager Comments:
                                </Typography>
                              </Box>
                              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                {session.manager_comments}
                              </Typography>
                            </Box>
                          )}

                          {/* PIP Plan Section */}
                          {hasPIPData(session) && session.status !== 'lead_assigned' && (
                            <>
                              <Divider sx={{ my: 2 }} />
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <WarningIcon sx={{ mr: 1, fontSize: 20, color: theme.palette.warning.main }} />
                                <Typography variant="h6" sx={{ fontSize: '0.95rem', fontWeight: 600, color: theme.palette.warning.main }}>
                                  Performance Improvement Plan
                                </Typography>
                              </Box>
                              
                              <Accordion 
                                expanded={expandedAccordion === `pip-${session.id}`}
                                onChange={() => setExpandedAccordion(
                                  expandedAccordion === `pip-${session.id}` ? null : `pip-${session.id}`
                                )}
                                sx={{ boxShadow: 'none', border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}` }}
                              >
                                <AccordionSummary 
                                  expandIcon={<ExpandMoreIcon />}
                                  sx={{ bgcolor: alpha(theme.palette.warning.main, 0.05) }}
                                >
                                  <Typography variant="body2" sx={{ fontSize: '0.875rem', color: theme.palette.warning.main, fontWeight: 500 }}>
                                    View PIP Details
                                  </Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ p: 2 }}>
                                  <Grid container spacing={{ xs: 1, sm: 2 }}>
                                    {/* Check if PIP has any data to display */}
                                    {(!pipData?.final_decision && !pipData?.location && !pipData?.pip_start_date && 
                                      !pipData?.pip_end_date && (!pipData?.attached_files || pipData?.attached_files?.length === 0) &&
                                      (!pipData?.dynamic_fields || Object.keys(pipData?.dynamic_fields || {}).length === 0)) ? (
                                      <Grid item xs={12}>
                                        <Box sx={{ p: 2, bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: 1, textAlign: 'center' }}>
                                          <InfoIcon sx={{ fontSize: 20, color: theme.palette.info.main, mb: 1 }} />
                                          <Typography variant="body2" sx={{ fontSize: '0.8rem', color: theme.palette.info.main, fontWeight: 500 }}>
                                            PIP Plan Created
                                          </Typography>
                                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.75rem' }}>
                                            No additional details have been added to this PIP plan yet.
                                          </Typography>
                                        </Box>
                                      </Grid>
                                    ) : (
                                      <>
                                        {pipData?.final_decision && (
                                          <Grid item xs={12} sm={6}>
                                            <Box sx={{ p: 1.5, bgcolor: alpha(theme.palette.error.main, 0.05), borderRadius: 1 }}>
                                              <Typography variant="caption" sx={{ color: theme.palette.error.main, fontWeight: 600, fontSize: '0.75rem' }}>
                                                Final Decision:
                                              </Typography>
                                              <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500, mt: 0.5 }}>
                                                {pipData?.final_decision}
                                              </Typography>
                                            </Box>
                                          </Grid>
                                        )}
                                    {pipData?.location && (
                                      <Grid item xs={12} sm={6}>
                                        <Box sx={{ p: 1.5, bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: 1 }}>
                                          <Typography variant="caption" sx={{ color: theme.palette.info.main, fontWeight: 600, fontSize: '0.75rem' }}>
                                            Location:
                                          </Typography>
                                          <Typography variant="body2" sx={{ fontSize: '0.8rem', mt: 0.5 }}>
                                            {pipData?.location}
                                          </Typography>
                                        </Box>
                                      </Grid>
                                    )}
                                    {(pipData?.pip_start_date || pipData?.pip_end_date) && (
                                      <Grid item xs={12}>
                                        <Box sx={{ p: 1.5, bgcolor: alpha(theme.palette.secondary.main, 0.05), borderRadius: 1 }}>
                                          <Typography variant="caption" sx={{ color: theme.palette.secondary.main, fontWeight: 600, fontSize: '0.75rem' }}>
                                            PIP Duration:
                                          </Typography>
                                          <Typography variant="body2" sx={{ fontSize: '0.8rem', mt: 0.5 }}>
                                            {formatDate(pipData?.pip_start_date)} - {formatDate(pipData?.pip_end_date)}
                                          </Typography>
                                        </Box>
                                      </Grid>
                                    )}
                                    {pipData?.release_date && (
                                      <Grid item xs={12} sm={6}>
                                        <Box sx={{ p: 1.5, bgcolor: alpha(theme.palette.success.main, 0.05), borderRadius: 1 }}>
                                          <Typography variant="caption" sx={{ color: theme.palette.success.main, fontWeight: 600, fontSize: '0.75rem' }}>
                                            Release Date:
                                          </Typography>
                                          <Typography variant="body2" sx={{ fontSize: '0.8rem', mt: 0.5 }}>
                                            {formatDate(pipData?.release_date)}
                                          </Typography>
                                        </Box>
                                      </Grid>
                                    )}
                                    {pipData?.attached_files && pipData.attached_files.length > 0 && (
                                      <Grid item xs={12}>
                                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600, fontSize: '0.75rem' }}>
                                          Attached Files:
                                        </Typography>
                                        <List dense sx={{ pt: 0.5 }}>
                                          {pipData.attached_files.map((file, fileIndex) => (
                                            <ListItem key={fileIndex} sx={{ px: 0, py: 0.5 }}>
                                              <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={() => window.open(typeof file === 'object' ? file.url : file, '_blank')}
                                                startIcon={<AttachFileIcon fontSize="small" />}
                                                sx={{
                                                  fontSize: '0.7rem',
                                                  textTransform: 'none',
                                                  borderColor: theme.palette.warning.main,
                                                  color: theme.palette.warning.main,
                                                  '&:hover': {
                                                    backgroundColor: alpha(theme.palette.warning.main, 0.1)
                                                  }
                                                }}
                                              >
                                                {typeof file === 'string' ? file : file.name}
                                              </Button>
                                            </ListItem>
                                          ))}
                                        </List>
                                      </Grid>
                                        )}
                                        {pipData?.dynamic_fields && Object.keys(pipData.dynamic_fields).length > 0 && (
                                      <Grid item xs={12}>
                                        <Box sx={{ p: 1.5, bgcolor: alpha(theme.palette.grey[600], 0.05), borderRadius: 1 }}>
                                          <Typography variant="caption" sx={{ color: theme.palette.grey[700], fontWeight: 600, fontSize: '0.75rem', mb: 1, display: 'block' }}>
                                            Additional PIP Details:
                                          </Typography>
                                          {Array.isArray(pipData?.dynamic_fields) ? (
                                            pipData.dynamic_fields.map((item, index) => (
                                              <Box key={index} sx={{ mb: 2 }}>
                                                <Typography variant="body2" sx={{ fontSize: '0.75rem', color: theme.palette.text.primary }}>
                                                  {renderValue(item)}
                                                </Typography>
                                              </Box>
                                            ))
                                          ) : (
                                            Object.entries(pipData?.dynamic_fields || {}).map(([key, value]) => (
                                              <Box key={key} sx={{ mb: 2 }}>
                                                <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600, color: theme.palette.text.secondary, display: 'block', mb: 0.5 }}>
                                                  {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontSize: '0.75rem', color: theme.palette.text.primary }}>
                                                  {renderValue(value)}
                                                </Typography>
                                              </Box>
                                            ))
                                          )}
                                        </Box>
                                        </Grid>
                                        )}
                                      </>
                                    )}
                                  </Grid>
                                </AccordionDetails>
                              </Accordion>
                            </>
                          )}
                        </CardContent>
                      </Card>
                    </Box>
                  </Stack>
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CommonHistoryDialog;